import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useDisclosure, useOutsideClick, useToast } from '@chakra-ui/react';

import { useMutation } from 'urql';
import { fromPairs, isNil } from 'lodash';
import invariant from 'tiny-invariant';
import { Row } from '@tanstack/react-table';
import {
  AggregatedOrderFilterType,
  EventResource,
  EventType,
  OrderColumnDeepFragment,
  OrderColumnPatch,
  OrderDeepFragment,
  ProjectDeepFragment,
  SheetShapeDeepFragment,
  UpdateOrderDocument,
  UpdateOrderInput,
} from '../../../gql/graphql';

import { useResolvedOrders } from '../../../hooks/order';
import { useLocalStorage } from '../../../hooks/local-storage';
import { useVisibility } from '../../../services/viewer/visibility';
import { mergeRecordOfLists } from '../../../services/viewer-services';
import { DbIdsPerModel } from '../../common/ForgeViewer';
import { extractFirstHexColor } from '../../../utils/color-util';
import { useColorization } from '../../../services/viewer/colorization';
import { useProjectHistory } from '../project-history/hooks/useProjectHistory';
import { HISTORY_COLORS } from '../project-history/hooks/useDiffing';
import { OrderEntryTableRow } from './aggregated-order-table/columns';
import { useAggregatedOrderTable } from './aggregated-order-table/useAggregatedOrderTable';
import { useGroupPopover } from './group-popover/useGroupPopover';
import { getAggEntryDiffPath } from './aggregated-order-table';
import { useEventHistory } from 'src/hooks/event-history';
import { useViewerMode, ViewerMode } from 'src/components/common/viewer-mode';
import { useEnabledSheetViewerModeValue } from 'src/components/viewers/common/hooks/useEnabledSheetViewerModeValue';
import { useEnabledSheetViewer } from 'src/components/viewers/common/hooks/useEnabledSheetViewer';
import { useSheetShapes } from 'src/hooks/sheet-shapes';
import { getSheetShapeUrn } from 'src/components/viewers/common/hooks/useSheetShapesManager';
import type { SheetViewerContextType } from 'src/components/common/SheetViewer';
import type { SheetsViewerV2ContextType } from 'src/components/viewers/SheetsViewerV2/SheetsViewerV2.context';

export function useAggregatedOrderPanel(
  order: OrderDeepFragment,
  project: ProjectDeepFragment
) {
  const ordersToResolve = useMemo(
    () => project.orders.filter((order) => !order.isAggregatedOrder),
    [project.orders]
  );

  const { resolvedOrders, isLoading } = useResolvedOrders(
    project.id,
    ordersToResolve
  );

  const toast = useToast();

  const {
    isOpen: showColumnManagementModal,
    onOpen: onColumnManagementModalOpen,
    onClose: onColumnManagementModalClose,
  } = useDisclosure();

  const {
    isGroupPopoverOpen,
    groupPopoverRef,
    grouping,
    shouldFlatten,
    isFlatteningLoading,
    toggleFlattenedStatus,
    setGrouping,
    onGroupPopoverToggle,
    onGroupPopoverClose,
    onGroupPopoverOpen,
  } = useGroupPopover({
    thisOrder: order,
  });

  const [isIsolationToggleActive, setIsolationToggleActive] =
    useLocalStorage<boolean>('isolate-linked-elements', true);

  const resizeableAreaRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Handle resizing of the panel
  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startY = event.clientY;
    const startHeight = panelRef.current?.clientHeight || 0;

    const doResize = (mouseMoveEvent: MouseEvent) => {
      const newHeight = Math.max(
        startHeight - (mouseMoveEvent.clientY - startY),
        100
      );
      if (panelRef.current) {
        panelRef.current.style.height = `${newHeight}px`;
      }
    };

    const stopResize = () => {
      window.removeEventListener('mousemove', doResize);
      window.removeEventListener('mouseup', stopResize);
    };

    window.addEventListener('mousemove', doResize);
    window.addEventListener('mouseup', stopResize);
  };

  const [selectedOrderEntries, setSelectedOrderEntries] = useState<
    OrderEntryTableRow[]
  >([]);

  const visibility = useVisibility();
  const { colorSection, clearColors } = useColorization();

  // Use refs to store the latest function references to prevent handleRowSelection from being recreated
  const visibilityRef = useRef(visibility);
  const colorizationRef = useRef({ colorSection, clearColors });
  const { setViewerMode } = useViewerMode();
  const { sheetsViewerMode } = useEnabledSheetViewerModeValue();
  const sheetViewer = useEnabledSheetViewer();
  const { shapes: sheetShapes } = useSheetShapes();

  const viewerModeRef = useRef({ setViewerMode, sheetsViewerMode });
  const sheetViewerActionsRef = useRef({
    changeActiveSheet: sheetViewer.changeActiveSheet,
    setPageNumber: sheetViewer.setPageNumber,
  });
  const sheetShapesRef = useRef(sheetShapes);
  const currentSheetStateRef = useRef({
    activeSheetId: (
      sheetViewer as SheetViewerContextType | SheetsViewerV2ContextType
    ).activeSheetId,
    pageNumber:
      'pagination' in
      (sheetViewer as SheetViewerContextType | SheetsViewerV2ContextType)
        ? (sheetViewer as SheetsViewerV2ContextType).pagination.pageNumber
        : (sheetViewer as SheetViewerContextType).pageNumber,
  });
  const previousUrnSignatureRef = useRef<string | null>(null);

  // Update refs when functions change
  useEffect(() => {
    visibilityRef.current = visibility;
  }, [visibility]);

  useEffect(() => {
    colorizationRef.current = { colorSection, clearColors };
  }, [colorSection, clearColors]);

  useEffect(() => {
    viewerModeRef.current = { setViewerMode, sheetsViewerMode };
  }, [setViewerMode, sheetsViewerMode]);

  useEffect(() => {
    sheetViewerActionsRef.current = {
      changeActiveSheet: sheetViewer.changeActiveSheet,
      setPageNumber: sheetViewer.setPageNumber,
    };
  }, [sheetViewer.changeActiveSheet, sheetViewer.setPageNumber]);

  useEffect(() => {
    sheetShapesRef.current = sheetShapes;
  }, [sheetShapes]);

  useEffect(() => {
    const ctx = sheetViewer as
      | SheetViewerContextType
      | SheetsViewerV2ContextType;
    const activeSheetId = ctx.activeSheetId;
    const pageNumber =
      'pagination' in ctx
        ? ctx.pagination.pageNumber
        : (ctx as SheetViewerContextType).pageNumber;
    currentSheetStateRef.current = { activeSheetId, pageNumber };
  }, [sheetViewer]);

  function isSheetUrn(urn: string): boolean {
    try {
      return atob(urn).includes('sheet-shape');
    } catch {
      return false;
    }
  }

  function getNonEmptyEntries(ids: DbIdsPerModel): [string, number[]][] {
    return Object.entries(ids).filter(([, dbIds]) => dbIds && dbIds.length > 0);
  }

  function getUniqueSheetsAndPages(entries: [string, number[]][]): {
    sheetIds: string[];
    pages: number[];
    pairs: Set<string>;
  } {
    const shapesData = sheetShapesRef.current?.data || [];
    const sheetIds: string[] = [];
    const pages: number[] = [];
    const pairs: Set<string> = new Set<string>();
    for (const [urn, dbIds] of entries) {
      for (const dbId of dbIds) {
        const shape = shapesData.find(
          (s: SheetShapeDeepFragment) =>
            getSheetShapeUrn(s.projectId) === urn && s.dbId === dbId
        );
        if (!shape) continue;
        if (!sheetIds.includes(shape.sheetId)) sheetIds.push(shape.sheetId);
        if (!pages.includes(shape.sheetPageNumber))
          pages.push(shape.sheetPageNumber);
        pairs.add(`${shape.sheetId}:${shape.sheetPageNumber}`);
      }
    }
    return { sheetIds, pages, pairs };
  }

  function buildDbIdsFromRows(
    selectedRows: OrderEntryTableRow[]
  ): DbIdsPerModel {
    let ids: DbIdsPerModel = {};
    for (const row of selectedRows) {
      ids = mergeRecordOfLists(
        ids,
        fromPairs(
          row.resolvedElementIdentifiersDbid?.map(({ modelUrn, dbIds }) => [
            modelUrn,
            dbIds,
          ])
        )
      );
    }
    return ids;
  }

  function createColorFunc(
    selectedRows: Row<OrderEntryTableRow>[],
    isHistoryMode: boolean,
    diff: Record<string, any> | null,
    allRows: Row<OrderEntryTableRow>[]
  ) {
    // eslint-disable-next-line complexity
    return (urn: string, dbId: number) => {
      if (isHistoryMode && diff) {
        // storing the colors for an element colorObj allows us to finish iterating the loop
        // finishing the loop allows us to find the correct color for the current urn,dbid pair
        const colorObj: Record<string, string> = {};
        const colorId = `${urn}-${dbId}`;
        const setColorForElement = (
          oldRawDbids: string | undefined,
          newRawDbids: string | undefined
        ) => {
          const oldDbids: Array<number> = JSON.parse(oldRawDbids ?? '[]');
          const newDbids: Array<number> = JSON.parse(newRawDbids ?? '[]');
          const inOld = oldDbids.find((oldDbid) => oldDbid === dbId);
          const inNew = newDbids.find((newDbid) => newDbid === dbId);
          if (inNew && !inOld) {
            // If in new and not in old then it has been added -> return the created color
            colorObj[colorId] = HISTORY_COLORS.CREATED;
          }
          if (!inNew && inOld) {
            // if in old and not in new, then it has been removed -> return the deleted color
            colorObj[colorId] = HISTORY_COLORS.DELETED;
          }
        };
        // shouldUseHistoricColor tells us if to gray out the models for contrast or skip and use the normal row colors
        let shouldUseHistoricColor = false;
        for (const row of selectedRows) {
          const orderId = row.original.thisOrderId;
          // we need the rows here and the
          const subKey = getAggEntryDiffPath(allRows, row.id);
          let collectionPath = `project.orders.${orderId}.orderEntries.${subKey}`;
          const collectionDiff = diff[collectionPath];
          if (collectionDiff) {
            // Means that this is a new object so we have to key into it
            shouldUseHistoricColor = true;
            const oldRawDbids =
              collectionDiff.old?.resolvedElementIdentifiersDbid?.[urn]?.dbIds;
            const newRawDbids =
              collectionDiff.new?.resolvedElementIdentifiersDbid?.[urn]?.dbIds;
            setColorForElement(oldRawDbids, newRawDbids);
          }
          const diffPath = `${collectionPath}.resolvedElementIdentifiersDbid.${urn}.dbIds`;
          const dbidDiff = diff[diffPath];
          if (dbidDiff) {
            shouldUseHistoricColor = true;
            setColorForElement(dbidDiff.old, dbidDiff.new);
          }
        }
        if (shouldUseHistoricColor) return colorObj[colorId] || '#dcdcdc';
      }

      for (const row of selectedRows.filter(
        (row) =>
          !(
            row.original.color?.includes('gradient') &&
            row.original.subRows &&
            row.original.subRows?.length > 0
          )
      )) {
        if (!row.original.resolvedElementIdentifiersDbid || !row.original.color)
          continue;
        if (
          row.original.resolvedElementIdentifiersDbid.some(
            (e) => e.modelUrn === urn && e.dbIds.includes(dbId)
          )
        ) {
          const color = extractFirstHexColor(row.original.color);
          if (color) return extractFirstHexColor(row.original.color);
        }
      }
      return '#000000';
    };
  }

  // eslint-disable-next-line complexity
  const switchViewerForIds = useCallback((ids: DbIdsPerModel) => {
    const nonEmptyEntries = getNonEmptyEntries(ids);

    // Build a stable signature of current non-empty URNs
    const signature =
      nonEmptyEntries.length === 0
        ? 'EMPTY'
        : nonEmptyEntries
            .slice()
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([urn, dbIds]) => `${urn}:${dbIds.length}`)
            .join('|');

    if (previousUrnSignatureRef.current === signature) {
      return;
    }
    previousUrnSignatureRef.current = signature;

    if (nonEmptyEntries.length === 0) {
      // No new URNs -> stay in current mode
      return;
    }

    const allSheetShapes = nonEmptyEntries.every(([urn]) => isSheetUrn(urn));
    const allNonSheetShapes = nonEmptyEntries.every(
      ([urn]) => !isSheetUrn(urn)
    );

    if (allSheetShapes) {
      viewerModeRef.current.setViewerMode(
        viewerModeRef.current.sheetsViewerMode
      );
      const { sheetIds, pages, pairs } =
        getUniqueSheetsAndPages(nonEmptyEntries);
      if (sheetIds.length > 0) {
        const { activeSheetId, pageNumber } = currentSheetStateRef.current;
        const isCurrentIncluded =
          activeSheetId !== null && pairs.has(`${activeSheetId}:${pageNumber}`);
        if (!isCurrentIncluded) {
          sheetViewerActionsRef.current.changeActiveSheet(sheetIds[0]);
          if (sheetIds.length === 1 && pages.length > 0) {
            sheetViewerActionsRef.current.setPageNumber(pages[0]);
          }
        }
      }
    } else if (allNonSheetShapes) {
      viewerModeRef.current.setViewerMode(ViewerMode.Models);
    } // else: mixed -> stay in current mode
  }, []);

  const { isHistoryDrawerOpen, diff } = useProjectHistory();

  const handleRowSelection = useCallback(
    (
      selectedRows: Row<OrderEntryTableRow>[],
      allRows: Row<OrderEntryTableRow>[]
    ) => {
      const originalSelectedRows = selectedRows.map((row) => row.original);
      setSelectedOrderEntries(originalSelectedRows);

      const ids = buildDbIdsFromRows(originalSelectedRows);

      if (isIsolationToggleActive) {
        visibilityRef.current.isolate(ids);
        switchViewerForIds(ids);
      }

      const colorFunc = createColorFunc(
        selectedRows,
        isHistoryDrawerOpen,
        diff,
        allRows
      );

      colorizationRef.current.clearColors();
      colorizationRef.current.colorSection(colorFunc, ids);
    },
    [isIsolationToggleActive, switchViewerForIds, isHistoryDrawerOpen, diff]
  );

  const [, updateOrder] = useMutation(UpdateOrderDocument);

  const memoizedOrders = useMemo(() => resolvedOrders ?? [], [resolvedOrders]);

  const {
    table,
    itemGroups,
    setRowSelection,
    handleExportToXlsx,
    renamingColumnId,
    renameColumn,
  } = useAggregatedOrderTable({
    resolvedOrders: memoizedOrders,
    thisOrder: order,
    project,
    isIsolationToggleActive,
    grouping,
    shouldFlatten,
    setGrouping,
    handleRowSelection,
  });

  const { logEvent } = useEventHistory(order.projectId);

  const handleOnFiltersChange = useCallback(
    async (filters: AggregatedOrderFilterType[]) => {
      const orderInput: UpdateOrderInput = {
        id: order.id,
        patch: {
          aggregatedOrderFilters: filters.map((filter) => ({
            columnName: filter.columnName,
            columnType: filter.columnType,
            predicate: filter.predicate,
            predicateValues: filter.predicateValues,
          })),
        },
      };

      const result = await updateOrder({
        input: orderInput,
      });

      if (result.error) {
        console.error(result.error);
      } else {
        logEvent({
          eventName: 'Table Filters Updated',
          eventType: EventType.Modified,
          resourceName: order.name,
          resourceType: EventResource.Order,
          metadata: {
            affectedResources: [order.id],
          },
        });
      }
    },
    [logEvent, order.id, order.name, updateOrder]
  );

  const handleOnFilterClear = useCallback(async () => {
    const orderInput: UpdateOrderInput = {
      id: order.id,
      patch: {
        aggregatedOrderFilters: [],
      },
    };

    const result = await updateOrder({
      input: orderInput,
    });

    if (result.error) {
      console.error(result.error);
    } else {
      logEvent({
        eventName: 'Table Filters Updated',
        eventType: EventType.Modified,
        resourceName: order.name,
        resourceType: EventResource.Order,
        metadata: {
          affectedResources: [order.id],
        },
      });
    }
  }, [logEvent, order.id, order.name, updateOrder]);

  const filterPopoverRef = useRef<HTMLDivElement | null>(null);
  const [isUsingCascader, setIsUsingCascader] = useState(false); // what the flip is a cascader?

  const {
    isOpen: isFilterPopoverOpen,
    onOpen: onFilterPopoverOpen,
    onClose: onFilterPopoverClose,
  } = useDisclosure();

  useOutsideClick({
    ref: filterPopoverRef,
    handler: () => {
      if (!isUsingCascader) {
        // Only close popover on outside click if no cascader is in use
        onFilterPopoverClose();
      }
    },
  });

  const handleColumnManagementSubmit = useCallback(
    async (orderCols: Array<OrderColumnPatch>) => {
      invariant(order, 'Order is not defined');
      const validOrderColumns = orderCols.filter(
        (orderCol) => !isNil(orderCol?.id)
      ) as OrderColumnDeepFragment[];

      // All valid order columns should exist in the DB should probably be an enforced invariant.

      const { error } = await updateOrder({
        input: {
          id: order.id,
          patch: {
            orderColumns: {
              updateById: validOrderColumns.map((col) => ({
                id: col.id,
                patch: {
                  name: col.name,
                  isVisible: col.isVisible,
                  columnOrder: col.columnOrder,
                },
              })),
            },
          },
        },
      });

      if (error) {
        toast({
          title: 'Error',
          description: 'Unable to save your column settings',
          status: 'error',
        });
        throw error;
      } else {
        logEvent({
          eventName: 'Columns Updated',
          eventType: EventType.Modified,
          resourceName: order.name,
          resourceType: EventResource.Order,
          metadata: {
            affectedResources: [order.id],
            details: `Columns updated in ${order.name}`,
          },
        });
      }
    },
    [order, updateOrder, toast, logEvent]
  );

  return {
    resolvedOrders,
    isIsolationToggleActive,
    selectedOrderEntries,
    isLoading: isLoading || resolvedOrders === null,
    table,
    isFilterPopoverOpen,
    resizeableAreaRef,
    filterPopoverRef,
    panelRef,
    showColumnManagementModal,
    itemGroups,
    isGroupPopoverOpen,
    groupPopoverRef,
    grouping,
    shouldFlatten,
    isFlatteningLoading,
    toggleFlattenedStatus,
    setGrouping,
    onGroupPopoverToggle,
    onGroupPopoverClose,
    onGroupPopoverOpen,
    onColumnManagementModalClose,
    onColumnManagementModalOpen,
    setRowSelection,
    setIsUsingCascader,
    onFilterPopoverClose,
    onFilterPopoverOpen,
    handleRowSelection,
    handleOnFilterClear,
    handleExportToXlsx,
    handleOnFiltersChange,
    handleMouseDown,
    setIsolationToggleActive,
    handleColumnManagementSubmit,
    renamingColumnId,
    renameColumn,
  };
}
